import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import { formatDate, formatTime, formatDateTime, parseUTC } from '../../utils/dateUtils';
import { usePhoneLimit, ShowMore } from '../../mobile';

// ============================================================
// Data Pull Log — every automatic pull from an outside system (VTS
// driver tow counts first): date, time, success or error, and the
// problems a person has to fix. Same idea as Sign Setup's "Fix failed
// pins": each problem says what's wrong and how to fix it, and the
// name-match ones are fixed right here. Super admin only.
// ============================================================

const RESULT = {
  success: { label: '✓ Success', bg: '#dcfce7', fg: '#166534' },
  warning: { label: '⚠ Needs a fix', bg: '#fef3c7', fg: '#92400e' },
  error: { label: '✗ Failed', bg: '#fee2e2', fg: '#991b1b' },
  running: { label: '⏳ Running', bg: '#dbeafe', fg: '#1e40af' },
};

const SOURCE_STATE = {
  on: { label: 'On', bg: '#dcfce7', fg: '#166534' },
  paused: { label: 'Paused — fix below', bg: '#fee2e2', fg: '#991b1b' },
  not_configured: { label: 'Login not set up', bg: '#fef3c7', fg: '#92400e' },
  not_built: { label: 'Being built', bg: '#f1f5f9', fg: '#475569' },
};

const TIMEFRAMES = [
  { days: 1, label: 'Today' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

// Compact cells — the log runs ~96 rows a day.
const cell = { padding: '10px 12px' };

const pill = (bg, fg) => ({
  display: 'inline-block', fontSize: 11.5, fontWeight: 700, padding: '3px 9px',
  borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap',
});

function ago(ts) {
  const d = parseUTC(ts);
  if (!d) return '—';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
  return `${Math.round(hrs / 24)} days ago`;
}

function Stat({ label, value, sub, accent }) {
  return (
    <div className="card" style={{ padding: '14px 18px', minWidth: 150, flex: 1 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent || 'var(--text-primary)', letterSpacing: '-0.01em' }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function DataPullLogPage() {
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState(null);
  const [days, setDays] = useState(7);
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [retrying, setRetrying] = useState('');

  const load = useCallback(async () => {
    try {
      const [ov, rr] = await Promise.all([
        api.getDataPullOverview(),
        api.getDataPullRuns({ days, problems: problemsOnly ? 1 : '' }),
      ]);
      setData(ov);
      setRuns(rr.runs);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [days, problemsOnly]);

  useEffect(() => { load(); }, [load]);
  // Pulls run every 15 min on the server — keep the page current.
  useEffect(() => {
    const t = setInterval(load, 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  async function retry(source) {
    setRetrying(source); setNotice(''); setError('');
    try {
      const { run } = await api.runDataPull(source);
      setNotice(run?.status === 'error' ? `Pull failed: ${run.error}` : `Pull finished: ${run?.summary || RESULT[run?.status]?.label || 'done'}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setRetrying('');
      load();
    }
  }

  if (error && !data) return <div className="admin-page"><p className="alert alert-error">{error}</p></div>;
  if (!data) return <div className="admin-page"><p>Loading…</p></div>;

  const { stats, sources, issues, recently_fixed: fixed } = data;
  const last = stats.last_run;
  const problemsToday = stats.failed_today + stats.warned_today;

  return (
    <div className="admin-page" style={{ maxWidth: 1240 }}>
      <div className="page-header">
        <h2>Data Pull Log</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
          Every automatic pull from outside systems — when it ran, whether it worked, and anything you need to fix.
        </p>
      </div>

      {error && <p className="alert alert-error">{error}</p>}
      {notice && <p className="alert" style={{ background: '#dcfce7', color: '#166534' }}>{notice}</p>}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Stat label="Last pull" value={last ? ago(last.started_at) : 'None yet'}
          sub={last ? RESULT[last.status]?.label : 'Starts once a source is hooked up'}
          accent={last ? (last.status === 'error' ? '#b91c1c' : last.status === 'warning' ? '#b45309' : undefined) : undefined} />
        <Stat label="Pulls today" value={stats.runs_today.toLocaleString('en-US')}
          sub={stats.last_success_at ? `Last success ${ago(stats.last_success_at)}` : 'No successful pull yet'} />
        <Stat label="Problems today" value={problemsToday.toLocaleString('en-US')}
          sub={`${stats.failed_today} failed · ${stats.warned_today} needed a fix`}
          accent={stats.failed_today > 0 ? '#b91c1c' : problemsToday > 0 ? '#b45309' : undefined} />
        <Stat label="Needs fixing" value={stats.open_issues.toLocaleString('en-US')}
          sub={stats.open_issues ? 'See the list below' : 'All clear'}
          accent={stats.open_issues > 0 ? '#b91c1c' : '#166534'} />
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        {sources.map(s => {
          const st = SOURCE_STATE[s.state] || SOURCE_STATE.not_built;
          return (
            <div key={s.key} className="card" style={{ padding: '12px 16px', flex: 1, minWidth: 280, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 800, fontSize: 14.5 }}>{s.label}</span>
                  <span style={pill(st.bg, st.fg)}>{s.running ? '⏳ Pulling now' : st.label}</span>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                  {s.what}{s.state === 'on' ? ` · every ${s.every_minutes} min` : ''}
                </div>
              </div>
              {s.state !== 'not_built' && (
                <button className="btn btn-muted" style={{ fontWeight: 600, fontSize: 12.5 }}
                  disabled={!!retrying || s.running} onClick={() => retry(s.key)}>
                  {retrying === s.key ? 'Pulling…' : 'Pull now'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.7fr)', gap: 16, alignItems: 'start' }} className="grid-stack-mobile">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <NeedsFixing issues={issues} onChanged={load} onRetry={retry} retrying={retrying} setNotice={setNotice} />
          {fixed.length > 0 && <RecentlyFixed rows={fixed} />}
        </div>
        <History runs={runs} days={days} setDays={setDays} problemsOnly={problemsOnly} setProblemsOnly={setProblemsOnly} />
      </div>
    </div>
  );
}

// ============================================================
// NeedsFixing — open problems, most urgent first. Name matches are fixed
// inline (pick the Apollo driver); login problems get Retry now; the rest
// get Mark fixed (they come back on the next pull if they weren't).
// ============================================================
function NeedsFixing({ issues, onChanged, onRetry, retrying, setNotice }) {
  const [drivers, setDrivers] = useState({});   // source → [{ id, full_name, board }]
  const [pick, setPick] = useState({});         // issue id → user id | 'ignore'
  const [busyId, setBusyId] = useState(null);
  const [result, setResult] = useState({});     // issue id → { ok, message }

  const needs = [...new Set(issues.filter(i => i.kind === 'unmatched_user').map(i => i.source))].join(',');
  useEffect(() => {
    needs.split(',').filter(Boolean).forEach(src => {
      api.getDataPullCandidates(src).then(r => setDrivers(d => ({ ...d, [src]: r.users }))).catch(() => {});
    });
  }, [needs]);

  async function act(issue, fn) {
    setBusyId(issue.id); setResult(r => ({ ...r, [issue.id]: null }));
    try {
      const res = await fn();
      setResult(r => ({ ...r, [issue.id]: { ok: true, message: res.message || 'Marked fixed' } }));
      setNotice(res.rerun ? 'Saved — pulling again now so the board catches up.' : 'Saved.');
      setTimeout(onChanged, 900);
    } catch (e) {
      setResult(r => ({ ...r, [issue.id]: { ok: false, message: e.message } }));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="card" style={{ padding: 18, borderLeft: `4px solid ${issues.length ? '#dc2626' : '#16a34a'}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <div style={{ fontWeight: 800, fontSize: 15 }}>🛠 Needs fixing</div>
        {issues.length > 0 && <span style={pill('#fee2e2', '#991b1b')}>{issues.length}</span>}
      </div>
      {issues.length === 0 && (
        <div style={{ fontSize: 13, color: '#065f46', fontWeight: 600 }}>Nothing to fix 🎉</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {issues.map(i => {
          const res = result[i.id];
          const list = drivers[i.source] || [];
          return (
            <div key={i.id} style={{ border: '1px solid var(--border-light, #e2e8f0)', borderRadius: 10, padding: '12px 14px', background: i.blocks_schedule ? '#fff7f7' : '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 13.5 }}>{i.title}</span>
                <span style={pill('#f1f5f9', '#334155')}>{i.source_label}</span>
                {!!i.blocks_schedule && <span style={pill('#fee2e2', '#991b1b')}>Pulls paused</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                First seen {formatDateTime(i.first_seen_at, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                {i.times_seen > 1 ? ` · seen on ${i.times_seen} pulls` : ''}
              </div>
              {i.detail && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 6 }}>{i.detail}</div>}
              <div style={{ fontSize: 12.5, color: '#1e3a8a', background: '#eff6ff', borderRadius: 8, padding: '7px 10px', marginTop: 8, lineHeight: 1.45 }}>
                💡 {i.fix_hint}
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
                {i.kind === 'unmatched_user' && (
                  <>
                    <select className="form-input" style={{ minWidth: 220, flex: 1, maxWidth: 340 }}
                      value={pick[i.id] || ''} onChange={e => setPick(p => ({ ...p, [i.id]: e.target.value }))}>
                      <option value="">Which Apollo driver is this?</option>
                      {list.map(u => <option key={u.id} value={u.id}>{u.full_name}{u.board ? ` — ${u.board}` : ''}</option>)}
                      <option value="ignore">Not a leaderboard driver — ignore this name</option>
                    </select>
                    <button className="btn btn-gold" style={{ fontSize: 12.5, fontWeight: 700 }}
                      disabled={!pick[i.id] || busyId === i.id}
                      onClick={() => act(i, () => api.matchDataPullIssue(i.id, pick[i.id] === 'ignore' ? { ignore: true } : { user_id: Number(pick[i.id]) }))}>
                      {busyId === i.id ? 'Saving…' : 'Save match'}
                    </button>
                  </>
                )}
                {(i.kind === 'login_failed' || i.kind === 'not_configured' || i.kind === 'keeps_failing') && (
                  <button className="btn btn-gold" style={{ fontSize: 12.5, fontWeight: 700 }}
                    disabled={!!retrying} onClick={() => onRetry(i.source)}>
                    {retrying === i.source ? 'Pulling…' : 'Retry now'}
                  </button>
                )}
                {i.kind !== 'unmatched_user' && i.kind !== 'not_configured' && (
                  <button className="btn btn-muted" style={{ fontSize: 12.5, fontWeight: 600 }}
                    disabled={busyId === i.id} onClick={() => act(i, () => api.markDataPullIssueFixed(i.id))}>
                    Mark fixed
                  </button>
                )}
              </div>
              {i.kind === 'unmatched_user' && list.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  No drivers are on a driver board yet — add them in Leaderboard → Setup → Groups first.
                </div>
              )}
              {res && (
                <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 600, color: res.ok ? '#065f46' : '#b91c1c' }}>
                  {res.ok ? '✓ ' : '✗ '}{res.message}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RecentlyFixed({ rows }) {
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Recently fixed</div>
      {rows.map(r => (
        <div key={r.id} style={{ padding: '7px 0', borderBottom: '1px solid var(--border-subtle, #edf2f7)' }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{r.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {r.resolution}{r.resolved_by_name ? ` · ${r.resolved_by_name}` : ''} · {formatDateTime(r.resolved_at, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// History — one row per pull. Tap a row for the full error / notes.
// ============================================================
function History({ runs, days, setDays, problemsOnly, setProblemsOnly }) {
  const [open, setOpen] = useState(null);
  const { shown, rest, more } = usePhoneLimit(runs, `${days}-${problemsOnly}`);

  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 10 }}>Pull history</div>
      <div className="m-chips">
        {TIMEFRAMES.map(t => (
          <button key={t.days} type="button" className={`m-chip${days === t.days ? ' active' : ''}`} onClick={() => setDays(t.days)}>
            {t.label}
          </button>
        ))}
        <button type="button" className={`m-chip${problemsOnly ? ' active' : ''}`} onClick={() => setProblemsOnly(p => !p)}>
          Problems only
        </button>
      </div>

      {runs === null && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>}
      {runs && runs.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>
          {problemsOnly ? 'No problems in this window.' : 'No pulls in this window yet.'}
        </div>
      )}
      {runs && runs.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>{['Date', 'Time', 'Source', 'Result', 'Details'].map(h => <th key={h} style={cell}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {shown.map(r => {
              const res = RESULT[r.status] || RESULT.running;
              const isOpen = open === r.id;
              const hasMore = r.error || r.details.length > 0;
              return (
                <tr key={r.id} onClick={() => hasMore && setOpen(isOpen ? null : r.id)} style={{ cursor: hasMore ? 'pointer' : 'default' }}>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}>{formatDate(r.started_at, { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}>{formatTime(r.started_at)}</td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                    {r.source_label}
                    {r.trigger === 'manual' && <div style={{ color: 'var(--text-muted)', fontSize: 11.5 }}>Pull now · {r.triggered_by_name || 'by hand'}</div>}
                  </td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}><span style={pill(res.bg, res.fg)}>{res.label}</span></td>
                  <td style={{ ...cell, fontSize: 12.5, minWidth: 200 }}>
                    <div style={{ color: r.status === 'error' ? '#b91c1c' : 'var(--text-secondary)' }}>
                      {r.status === 'error' ? r.error : (r.summary || '—')}
                      {r.issue_count > 0 && r.status !== 'error' ? ` · ${r.issue_count} to fix` : ''}
                      {hasMore && <span style={{ color: 'var(--text-faint)' }}> {isOpen ? '▴' : '▾'}</span>}
                    </div>
                    {isOpen && (
                      <div style={{ marginTop: 6, padding: '8px 10px', background: 'var(--bg-inset, #f1f5f9)', borderRadius: 8, color: 'var(--text-secondary)' }}>
                        {r.error && r.summary && <div>{r.summary}</div>}
                        {r.details.map((d, idx) => <div key={idx}>{d}</div>)}
                        {r.duration_ms != null && <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>Took {(r.duration_ms / 1000).toFixed(1)}s</div>}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
      <ShowMore rest={rest} onMore={more} />
    </div>
  );
}
